import { JobStatus, JobTypes } from 'BMapsSrc/jobs/JobDefinition';
import { JobManagerRequestTypes, JobManagerResponseTypes } from 'BMapsSrc/jobs/jobs_protocol';
import { sleep } from 'BMapsSrc/util/js_utils';
import { mockReinvent4Results } from './mock_reinvent4_results';

const mockFoldingData = require('./mock_folding_results.json');
const mockFoldingError = require('./mock_folding_error.json');
const mockMapLightData = require('./mock_maplight_results.json');
const mockMapLightError = require('./mock_maplight_error.json');
const mockScreeningData = require('./mock_boltz_screening_results.json');

const failJobCreationRequest = false;

function minutesAgo(min) {
    const ms = min * 60000;
    return new Date(Date.now() - ms).toISOString();
}

const oneMinuteAgo = minutesAgo(1);
const twoMinutesAgo = minutesAgo(2);
const threeMinutesAgo = minutesAgo(3);

const initialJobsState = [{
    jobid: 'job-001',
    jobtype: JobTypes.FOLDING,
    state: JobStatus.SUCCEEDED,
    done: twoMinutesAgo,
    created: threeMinutesAgo,
    delivered: null,
    type: 'Job',
    requestmeta: { moleculeName: 'Example Boltz Job 1' },
    jobrequest: getExampleBoltzFoldingArgs(),
}, {
    jobid: 'job-002',
    jobtype: JobTypes.FOLDING,
    state: JobStatus.SUCCEEDED,
    created: threeMinutesAgo,
    done: twoMinutesAgo,
    delivered: oneMinuteAgo,
    type: 'Job',
    requestmeta: { moleculeName: 'Example Boltz Job 2' },
    jobrequest: getExampleBoltzFoldingArgs(),
}, {
    jobid: 'job-003',
    jobtype: JobTypes.FOLDING,
    state: JobStatus.SUCCEEDED,
    done: oneMinuteAgo,
    created: twoMinutesAgo,
    delivered: null,
    type: 'Job',
    requestmeta: { moleculeName: 'Example Boltz Job 3' },
    jobrequest: getExampleBoltzFoldingArgs(),
}, {
    jobid: 'job-004',
    jobtype: JobTypes.FOLDING,
    state: JobStatus.FAILED,
    done: twoMinutesAgo,
    created: threeMinutesAgo,
    delivered: null,
    type: 'Job',
    requestmeta: { moleculeName: 'Example Boltz Job 4' },
    jobrequest: getExampleBoltzFoldingArgs(),
}, {
    jobid: 'job-005',
    jobtype: JobTypes.REINVENT,
    state: JobStatus.SUCCEEDED,
    done: twoMinutesAgo,
    created: threeMinutesAgo,
    delivered: null,
    type: 'Job',
    requestmeta: {},
    jobrequest: getExampleReinvent4Args(),
}, {
    jobid: 'job-006',
    jobtype: JobTypes.LIBRARY_SCREEN,
    state: JobStatus.SUCCEEDED,
    done: twoMinutesAgo,
    created: threeMinutesAgo,
    delivered: null,
    type: 'Job',
    requestmeta: { jobName: 'Example Screening Job 1' },
    jobrequest: getExampleBoltzScreeningArgs(),
}, {
    jobid: 'job-007',
    jobtype: JobTypes.REINVENT,
    state: JobStatus.SUCCEEDED,
    done: twoMinutesAgo,
    created: threeMinutesAgo,
    delivered: null,
    type: 'Job',
    requestmeta: { jobName: 'False Positive Job' },
    jobrequest: getExampleReinvent4Args(),
    testDetails: {
        falsePositive: true,
    },

}];

export class MockJobManager {
    constructor(initial = initialJobsState) {
        this.jobs = initial.map((j) => new Job(j));
    }

    getJobs() {
        return this.jobs.map((j) => j.getJobsListResponse());
    }

    getResultForJob(request) {
        const jobid = request.jobid;
        const job = this.jobs.find((j) => j.jobid === jobid);
        return job.getJobResult();
    }

    createJob(request) {
        if (failJobCreationRequest) {
            return {
                error: {
                    report: 'The mock job request failed as requested',
                    detail: 'More detailed information about how the mock job creation request failed as requested',
                },
            };
        }
        const jobid = `job-${this.jobs.length + 1}`;
        const jobType = request.jobtype;
        const state = JobStatus.RUNNING;
        const type = 'Job';
        const requestmeta = request.requestmeta;
        const stash = request.stash;
        const jobrequest = request.jobrequest;
        const created = minutesAgo(0);

        const job = new Job({
            jobid, jobtype: jobType, state, type, requestmeta, stash, jobrequest, created,
        });
        job.workOnJob();

        this.jobs.push(job);
        return {
            type: JobManagerResponseTypes.JOB_CREATED,
            jobid,
            jobtype: jobType,
            state: JobStatus.STARTING,
            requestmeta,
        };
    }

    deleteJob(request) {
        const jobid = request.jobid;
        const job = this.jobs.find((j) => j.jobid === jobid);
        this.jobs = this.jobs.filter((j) => j.jobid !== jobid);
        return {
            type: JobManagerResponseTypes.JOB_DELETED,
            jobid,
            success: !!job,
        };
    }
}

class Job {
    constructor({
        jobid, jobtype, state, done, delivered, type,
        requestmeta, stash, jobrequest, created, testDetails,
    }) {
        this.jobid = jobid;
        this.jobtype = jobtype;
        this.state = state;
        this.done = done;
        this.delivered = delivered || null;
        this.type = type;
        this.requestmeta = requestmeta;
        this.stash = stash;
        this.jobrequest = jobrequest;
        this.created = created;
        this.testDetails = testDetails || {};
    }

    getJobsListResponse() {
        const type = this.type;
        const jobid = this.jobid;
        const jobtype = this.jobtype;
        const state = this.state;
        const done = this.done;
        const delivered = this.delivered;
        const requestmeta = this.requestmeta;
        const created = this.created;
        return {
            type, jobid, jobtype, state, done, delivered, requestmeta, created,
        };
    }

    getJobResult() {
        const jobResult = {
            type: JobManagerResponseTypes.JOB_RESULTS,
            jobid: this.jobid,
            state: this.state,
            jobrequest: this.jobrequest || {},
            error: null,
            stash: this.stash || {},
        };

        if (this.testDetails.falsePositive) {
            // Update the "server-side" job
            this.state = JobStatus.FAILED;
            // Return early with overridden job results
            jobResult.error = {
                report: 'job results were missing',
                detail: '(Code 103): job results were missing',
            };
            jobResult.state = JobStatus.FAILED;
            jobResult.jobresult = null;
            return jobResult;
        }

        switch (this.jobtype) {
            case JobTypes.FOLDING:
                jobResult.jobresult = this.getFoldingResult().jobresult;
                jobResult.error = this.getFoldingResult().error;
                break;
            case JobTypes.MAPLIGHT:
                jobResult.jobresult = this.getMapLightResult().jobresult;
                jobResult.error = this.getMapLightResult().error;
                break;
            case JobTypes.REINVENT:
                jobResult.jobresult = this.getReinvent4Result().jobresult;
                jobResult.error = this.getReinvent4Result().error;
                break;
            case JobTypes.LIBRARY_SCREEN:
                jobResult.jobresult = this.getBoltzScreeningResult().jobresult;
                jobResult.error = this.getBoltzScreeningResult().error;
                break;
            default: jobResult.jobresult = {};
        }
        this.delivered = minutesAgo(0);
        return jobResult;
    }

    getBoltzScreeningResult() {
        if (this.state === JobStatus.SUCCEEDED) {
            return { jobresult: mockScreeningData };
        }
        if (this.state === JobStatus.FAILED) {
            return { error: 'error' };
        }
        return {};
    }

    getFoldingResult() {
        if (this.state === JobStatus.SUCCEEDED) {
            return { jobresult: mockFoldingData };
        }
        if (this.state === JobStatus.FAILED) {
            return { error: mockFoldingError };
        }
        return {};
    }

    getMapLightResult() {
        const compoundNames = Object.keys(this.jobrequest);
        if (this.state === JobStatus.SUCCEEDED) {
            const result = {};
            for (const compoundName of compoundNames) {
                result[compoundName] = mockMapLightData;
            }
            return { jobresult: result };
        }
        if (this.state === JobStatus.FAILED) {
            return { error: mockMapLightError };
        }
        return {};
    }

    getReinvent4Result() {
        if (this.state === JobStatus.SUCCEEDED) {
            return { jobresult: { results: mockReinvent4Results } };
        }
        return { error: 'Failure to load Reinvent results' };
    }

    async workOnJob(timeout=50, failure=false) {
        await sleep(timeout);
        this.done = minutesAgo(0);
        if (failure) {
            this.state = JobStatus.FAILED;
            return;
        }
        this.state = JobStatus.SUCCEEDED;
    }
}

export function getExampleBoltzFoldingArgs() {
    return {
        foldingProgramId: 'Boltz-2',
        foldingArgs: {
            foldingProgramId: 'Boltz-2',
            boltz_args: {
                use_cpu: false,
                json: {
                    sequences: [
                        {
                            protein: {
                                id: 'P0',
                                sequence: 'MAEPDPSHPLETQAGKVQEAQDSDSDSEGGAAGGEADMDFLRNLFSQTLSLGSQKERLLDELTLEGVARYMQSERCRRVICLVGAGISTSAGIPDFRSPSTGLYDNLEKYHLPYPEAIFEISYFKKHPEPFFALAKELYPGQFKPTICHYFMRLLKDKGLLLRCYTQNIDTLERIAGLEQEDLVEAHGTFYTSHCVSASCRHEYPLSWMKEKIFSEVTPKCEDCQSLVKPDIVFFGESLPARFFSCMQSDFLKVDLLLVMGTSLQVQPFASLISKAPLSTPRLLINKEKAGQSDPFLGMIMGLGGGMDFDSKKAYRDVAWLGECDQGCLALAELLGWKKELEDLVRREHASIDAQSGAGVPNPSTSASPKKSPPPAKDEARTTEREKPQ',
                            },
                        },
                        {
                            ligand: {
                                id: 'L1',
                                smiles: 'Cc1cn(c(n1)C)c2ccc3c(c2)CN(CC3)c4ncc(s4)Cc5ccc(cc5)n6cccn6',
                            },
                        },
                        {
                            ligand: {
                                id: 'L2',
                                ccd: 'ZN',
                            },
                        },
                    ],
                    properties: [
                        {
                            affinity: {
                                binder: 'L1',
                            },
                        },
                    ],
                },
            },
            use_protein_prep: false,
        },
    };
}

export function getExampleReinvent4Args() {
    return {
        smiles: ['C1=CC=CC=C1'],
    };
}

export function getExampleBoltzScreeningArgs() {
    return {
        cmopounds: [
            { id: 'cmpd1', smiles: 'C1=CC=CC=C1' },
            { id: 'cmpd2', smiles: 'c1ccccc1' },
        ],
        library: {
            id: 'all-human-kinases',
        },
        screen_type: {
            jobtype: 'folding',
            subtype: 'Boltz-2',
        },
    };
}
export function getFoldingRequest() {
    return {
        type: JobManagerRequestTypes.JOB_REQUEST,
        jobtype: JobTypes.FOLDING,
        jobrequest: getExampleBoltzFoldingArgs(),
        stash: {},
        requestmeta: {},
    };
}

export function getCompoundGenerationRequest() {
    return {
        type: JobManagerRequestTypes.JOB_REQUEST,
        jobtype: JobTypes.REINVENT,
        jobrequest: getExampleReinvent4Args(),
        stash: {},
        requestmeta: {},
    };
}

export function getScreeningRequest() {
    return {
        type: JobManagerRequestTypes.JOB_REQUEST,
        jobtype: JobTypes.LIBRARY_SCREEN,
        jobrequest: getExampleBoltzScreeningArgs(),
        stash: {},
        requestmeta: { jobName: 'Test Screening Job' },
    };
}
